import MarkdownIt from 'markdown-it';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { useStreamAgent } from '@treams/treams-hooks/useStreamAgent';
import * as graphql from '@treams/treams-network';
import { AICard } from '@treams/treams-ui/common/AICard';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { MarkdownStyled } from '@treams/treams-ui/common/MarkdownStyled';
import { Skeleton } from '@treams/treams-ui/common/Skeleton';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { useSmoothedText } from '../../../talent-reviews/v2/components/smoothedText';
import { type ScoreInsightsInput, countNumberedItems } from '../scoreInsights';

const markdown = new MarkdownIt({ breaks: true, linkify: true });

const InsightsLoading = () => {
	const { t } = useTranslation();

	return (
		<FlexGap
			flexDirection="column"
			gap={tokens.gap.element}
			role="status"
			aria-label={t('analytics.treamsScore.insights.loading')}
		>
			<Skeleton.Box tint="purple" height={12} width="100%" />
			<Skeleton.Box tint="purple" height={12} width="92%" />
			<Skeleton.Box tint="purple" height={12} width="78%" />
		</FlexGap>
	);
};

export function TreamsScoreInsights(props: { cacheKey: string; input: ScoreInsightsInput }) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const { stream, text } = useStreamAgent();
	const [collapsed, setCollapsed] = useState(false);
	const [streaming, setStreaming] = useState(false);
	const [activeKey, setActiveKey] = useState<string | null>(null);
	const [failedKey, setFailedKey] = useState<string | null>(null);
	const [cache, setCache] = useState<Record<string, string>>({});

	// Scores refresh when the interval changes without changing the selection;
	// only a new selection (cacheKey) should ask for new insights.
	// Insights are cached per selection only, so a later locale change keeps them.
	const requestRef = useRef({ input: props.input, locale: language.id });
	requestRef.current = { input: props.input, locale: language.id };

	const cached = Object.hasOwn(cache, props.cacheKey);
	const failed = !cached && failedKey === props.cacheKey;
	const isCurrentStream = activeKey === props.cacheKey;
	const rawText = cached ? cache[props.cacheKey] : isCurrentStream ? text : '';
	const displayText = useSmoothedText(rawText, streaming && isCurrentStream && !cached);
	const displayHtml = useMemo(
		() => (displayText ? markdown.render(displayText) : ''),
		[displayText],
	);
	const loading = streaming && isCurrentStream && !displayText;
	const tipCount = countNumberedItems(displayText);

	useEffect(() => {
		if (!hasAI || cached || language.loading) {
			return;
		}

		const controller = new AbortController();
		const cacheKey = props.cacheKey;
		setActiveKey(cacheKey);
		setStreaming(true);
		setFailedKey(null);

		stream(
			graphql.Prompt.TreamsScoreInsights,
			{
				scores: JSON.stringify(requestRef.current.input),
				locale: requestRef.current.locale,
			},
			controller.signal,
		)
			.then((accumulated) => {
				if (controller.signal.aborted) {
					return;
				}

				setCache((current) => ({ ...current, [cacheKey]: accumulated ?? '' }));
			})
			.catch((error) => {
				if (error instanceof DOMException && error.name === 'AbortError') {
					return;
				}

				if (!controller.signal.aborted) {
					setFailedKey(cacheKey);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setStreaming(false);
				}
			});

		return () => {
			controller.abort();
			setStreaming(false);
		};
		// stream identity is stable per workspace; listing it retriggers on parent renders.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasAI, cached, props.cacheKey, language.loading]);

	if (!hasAI || failed || (!streaming && !displayText)) {
		return null;
	}

	return (
		<Box mt={4}>
			<AICard
				title={t('analytics.treamsScore.insights.title', { name: props.input.selection })}
				count={tipCount > 0 ? tipCount : undefined}
				collapsed={collapsed}
				onHeaderClick={() => setCollapsed((current) => !current)}
			>
				{loading && <InsightsLoading />}
				{displayHtml && (
					<span aria-live="polite">
						<MarkdownStyled dangerouslySetInnerHTML={{ __html: displayHtml }} />
					</span>
				)}
			</AICard>
		</Box>
	);
}
